import { Box, Typography } from "@mui/material";
import React, { useContext, useEffect } from "react";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import { FleetContext } from "../../shared/components/page_base";
import { useParams } from "react-router-dom";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "& td": {
    backgroundColor: "#E0E3E7",
    border: 0,
  },
  "& th": {
    backgroundColor: "#35393F",
    border: 0,
    padding: "0.25rem",
  },
  "& td:first-of-type, & th:first-of-type": {
    borderTopLeftRadius: "2px",
    borderBottomLeftRadius: "2px",
  },
  "& td:last-of-type, & th:last-of-type": {
    borderTopRightRadius: "2px",
    borderBottomRightRadius: "2px",
  },
}));

const robotColors = [
  "#9e0142",
  "#f46d43",
  "#5e4fa2",
  "#3288bd",
  "#d53e4f",
  "#fdae61",
  "#e6f598",
  "#66c2a5",
  "#abdda4",
];

function RobotSummary(props) {
  const {
    data,
    displayedRobots,
    setDisplayedRobots,
    robotSummaryData,
    setRobotSummaryData,
  } = props;
  let { fleetId } = useParams();
  const { robotStates } = useContext(FleetContext);

  useEffect(() => {
    let summaryData = {};

    if (!robotStates || robotStates.size === 0) {
      return;
    }

    /// Set default data so even if a robot doesn't have any report data, it's still displayed
    let colorIndex = 0;
    const tmp = new Map();
    robotStates.forEach((robot, robot_id) => {
      if (robot.fleet_id === fleetId) {
        tmp.set(robot_id, {
          show: true,
          color: robotColors[colorIndex],
        });
        colorIndex++;
        if (colorIndex === robotColors.length) {
          colorIndex = 0;
        }
        summaryData[robot_id] = {
          robot_name: robot.name,
          total_incidents: 0,
          total_run_time: 0,
          total_idle_duration: 0,
          total_destinations_reached: 0,
          total_distance_travelled: 0,
        };
      }
    });
    console.log("setDisplayedRobots", tmp);
    setDisplayedRobots(tmp);

    if (!data) {
      setRobotSummaryData(summaryData);
      return;
    }

    if ("incidents" in data) {
      data["incidents"].forEach((incident) => {
        if (
          incident["robot_id"] in summaryData &&
          "total_incidents" in summaryData[incident["robot_id"]]
        ) {
          summaryData[incident["robot_id"]]["total_incidents"] += 1;
        } else {
          summaryData[incident["robot_id"]] = {
            robot_name: incident["robot_name"],
            total_incidents: 1,
            total_run_time: 0,
            total_idle_duration: 0,
            total_destinations_reached: 0,
            total_distance_travelled: 0,
          };
        }
      });
    }

    if ("run_times" in data) {
      data["run_times"].forEach((run_time) => {
        if (run_time["robot_id"] in summaryData) {
          if ("total_run_time" in summaryData[run_time["robot_id"]]) {
            summaryData[run_time["robot_id"]]["total_run_time"] +=
              run_time["user_auto_duration"] +
              run_time["user_manual_duration"] +
              run_time["operator_auto_duration"] +
              run_time["cleaning_duration"];
          } else {
            summaryData[run_time["robot_id"]]["total_run_time"] =
              run_time["user_auto_duration"] +
              run_time["user_manual_duration"] +
              run_time["operator_auto_duration"] +
              run_time["cleaning_duration"];
          }
          if ("total_idle_duration" in summaryData[run_time["robot_id"]]) {
            summaryData[run_time["robot_id"]]["total_idle_duration"] +=
              run_time["idle_duration"];
          } else {
            summaryData[run_time["robot_id"]]["total_idle_duration"] =
              run_time["idle_duration"];
          }
        } else {
          summaryData[run_time["robot_id"]] = {
            robot_name: run_time["robot_name"],
            total_incidents: 0,
            total_run_time:
              run_time["user_auto_duration"] +
              run_time["user_manual_duration"] +
              run_time["operator_auto_duration"] +
              run_time["cleaning_duration"],
            total_idle_duration: run_time["idle_duration"],
            total_destinations_reached: 0,
            total_distance_travelled: 0,
          };
        }
      });
    }

    if ("tasks" in data) {
      data["tasks"].forEach((task) => {
        if (task["robot_id"] in summaryData) {
          if ("total_destinations_reached" in summaryData[task["robot_id"]]) {
            let totalReached = 0;
            if (task["status"] === "done") {
              totalReached = task["sub_tasks"].length;
            }
            summaryData[task["robot_id"]]["total_destinations_reached"] +=
              totalReached;
          } else {
            let totalReached = 0;
            if (task["status"] === "done") {
              totalReached = task["sub_tasks"].length;
            }
            summaryData[task["robot_id"]]["total_destinations_reached"] =
              totalReached;
          }
          if ("total_distance_travelled" in summaryData[task["robot_id"]]) {
            let distanceTravelled = 0;
            task["sub_tasks"].forEach((sub_task) => {
              distanceTravelled += sub_task["distance_travelled"];
            });
            summaryData[task["robot_id"]]["total_distance_travelled"] +=
              distanceTravelled;
          } else {
            let distanceTravelled = 0;
            task["sub_tasks"].forEach((sub_task) => {
              distanceTravelled += sub_task["distance_travelled"];
            });
            summaryData[task["robot_id"]]["total_distance_travelled"] =
              distanceTravelled;
          }
        } else {
          let distanceTravelled = 0;
          task["sub_tasks"].forEach((sub_task) => {
            distanceTravelled += sub_task["distance_travelled"];
          });

          let totalReached = 0;
          if (task["status"] === "done") {
            totalReached = task["sub_tasks"].length;
          }

          summaryData[task["robot_id"]] = {
            robot_name: task["robot_name"],
            total_incidents: 0,
            total_run_time: 0,
            total_idle_duration: 0,
            total_destinations_reached: totalReached,
            total_distance_travelled: distanceTravelled,
          };
        }
      });
    }

    for (const key in summaryData) {
      summaryData[key]["total_run_time"] = new Date(
        summaryData[key]["total_run_time"] * 1000
      )
        .toISOString()
        .slice(11, 19);
      summaryData[key]["total_idle_duration"] = new Date(
        summaryData[key]["total_idle_duration"] * 1000
      )
        .toISOString()
        .slice(11, 19);
    }

    console.log("summaryData", summaryData);

    setRobotSummaryData(summaryData);
  }, [data, fleetId, robotStates, setDisplayedRobots, setRobotSummaryData]);

  const checkedRobot = (robotId) => {
    let value = displayedRobots.get(robotId);
    value.show = !value.show;
    setDisplayedRobots(new Map(displayedRobots.set(robotId, value)));
  };

  return (
    <React.Fragment>
      <Box
        display="flex"
        sx={{
          flexDirection: "column",
          flexGrow: 1,
          marginTop: "1.5rem",
          marginLeft: "1.5rem",
          marginRight: "1.5rem",
        }}
      >
        <TableContainer component={Box}>
          <Table
            sx={{
              borderCollapse: "separate",
              borderSpacing: "0 0.75rem",
              marginTop: "-0.75rem",
            }}
          >
            <TableHead>
              <StyledTableRow>
                <TableCell align="center"></TableCell>
                <TableCell align="center">
                  <Typography variant="h6" color="white" fontWeight="700">
                    Robot Name
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6" color="white" fontWeight="700">
                    Total Run Time
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6" color="white" fontWeight="700">
                    Total Idle Time
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6" color="white" fontWeight="700">
                    Total # of Incidents
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6" color="white" fontWeight="700">
                    Total Distance Travelled
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography variant="h6" color="white" fontWeight="700">
                    Total # of Destinations Reached
                  </Typography>
                </TableCell>
              </StyledTableRow>
            </TableHead>
            <TableBody>
              {Object.entries(robotSummaryData).map(([key, value]) => (
                <StyledTableRow key={key}>
                  <TableCell align="center" scope="row">
                    <Checkbox
                      checked={
                        displayedRobots.has(key)
                          ? displayedRobots.get(key).show
                          : true
                      }
                      onChange={() => {
                        checkedRobot(key);
                      }}
                      inputProps={{ "aria-label": "controlled" }}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={<CheckCircleIcon />}
                      sx={{
                        color: displayedRobots.has(key)
                          ? displayedRobots.get(key).color
                          : "black",
                        "&.Mui-checked": {
                          color: displayedRobots.has(key)
                            ? displayedRobots.get(key).color
                            : "black",
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="center" scope="row">
                    <Typography variant="h6" fontWeight="400">
                      {value["robot_name"]}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6" fontWeight="400">
                      {value["total_run_time"]}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6" fontWeight="400">
                      {value["total_idle_duration"]}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6" fontWeight="400">
                      {value["total_incidents"]}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6" fontWeight="400">
                      {value["total_distance_travelled"].toLocaleString(
                        undefined,
                        {
                          maximumFractionDigits: 0,
                        }
                      )}{" "}
                      meters
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="h6" fontWeight="400">
                      {value["total_destinations_reached"]}
                    </Typography>
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </React.Fragment>
  );
}

export default RobotSummary;
